// src/components/Sidebar.js
//
// Vertical navigation for logged-in users. Replaces the top nav for
// authenticated sessions (HeaderNav returns null in that state and only
// renders the minimal branded top bar for /auth). Collapses to icons on
// desktop and slides in as a drawer on mobile.
//
// Structure — 5 groups + user zone:
//   • Panel               (home dashboard)
//   • Clientes            (single item)
//   • Operaciones ▾       (Equipo, Productos, Proveedores, Costos)
//   • Proyectos ▾         (General, Bundle, Detalles, Líderes, Con Causa,
//                          Value Stream, Pagos)
//   • Finanzas ▾          (Movimientos Bancarios, Box Score, Analytics)
//   • AI ▾                (Asistente, Log — visibility gated by API)
//   • User zone at bottom (Security, Sign Out)
//
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { API_URL_chatbot_access } from '../constants';
import { getAuthHeaders, getCurrentUser, logout } from '../utils/auth';

const SIDEBAR_WIDTH_EXPANDED = 240;
const SIDEBAR_WIDTH_COLLAPSED = 64;
const BRAND_RED = '#dc3545';
const BRAND_RED_DARK = '#a01e2b';

const STORAGE_KEY = 'lssi-sidebar-collapsed';

// --- SVG icons (16px viewbox, currentColor) ------------------------------
const Icon = ({ children, size = 20 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    style={{ flexShrink: 0 }}
  >
    {children}
  </svg>
);
const IconPanel = () => <Icon><rect x="3" y="3" width="7" height="9" /><rect x="14" y="3" width="7" height="5" /><rect x="14" y="12" width="7" height="9" /><rect x="3" y="16" width="7" height="5" /></Icon>;
const IconClientes = () => <Icon><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></Icon>;
const IconOperaciones = () => <Icon><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" /></Icon>;
const IconProyectos = () => <Icon><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" /></Icon>;
const IconFinanzas = () => <Icon><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></Icon>;
const IconAI = () => <Icon><rect x="3" y="11" width="18" height="10" rx="2" ry="2" /><circle cx="12" cy="5" r="2" /><path d="M12 7v4" /><line x1="8" y1="16" x2="8" y2="16" /><line x1="16" y1="16" x2="16" y2="16" /></Icon>;
const IconChevron = ({ open }) => (
  <svg
    width={14}
    height={14}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ transition: 'transform 0.2s', transform: open ? 'rotate(90deg)' : 'rotate(0deg)', flexShrink: 0 }}
    aria-hidden="true"
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
);
const IconMenu = () => <Icon><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="18" x2="21" y2="18" /></Icon>;
const IconLogOut = () => <Icon size={16}><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></Icon>;
const IconShield = () => <Icon size={16}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></Icon>;

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [loggedInUser, setLoggedInUser] = useState(getCurrentUser() || null);
  const [chatbotAllowed, setChatbotAllowed] = useState(false);
  const [chatbotAdmin, setChatbotAdmin] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return window.localStorage.getItem(STORAGE_KEY) === '1';
    } catch (_) {
      return false;
    }
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState({
    operaciones: false,
    proyectos: false,
    finanzas: false,
    ai: false,
  });

  useEffect(() => {
    const user = getCurrentUser();
    setLoggedInUser(user || null);

    if (user) {
      axios
        .get(API_URL_chatbot_access, { headers: getAuthHeaders() })
        .then((res) => {
          setChatbotAllowed(!!res.data?.allowed);
          setChatbotAdmin(!!res.data?.is_admin);
        })
        .catch(() => {
          setChatbotAllowed(false);
          setChatbotAdmin(false);
        });
    }
  }, [location]);

  // Broadcast the sidebar's effective width via a CSS custom property so page
  // content can shift left of it without every page component having to know.
  useEffect(() => {
    const width = mobileOpen ? 0 : collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED;
    document.documentElement.style.setProperty('--lssi-sidebar-width', `${width}px`);
    // Body-level flag so we can trigger the mobile-vs-desktop branch in CSS.
    document.body.classList.toggle('lssi-sidebar-collapsed', collapsed);
    return () => {
      document.documentElement.style.removeProperty('--lssi-sidebar-width');
    };
  }, [collapsed, mobileOpen]);

  // Auto-open the group that matches the current route so the active item is
  // always visible on load. Runs once per pathname change.
  useEffect(() => {
    const path = location.pathname;
    setOpenGroups((prev) => {
      const next = { ...prev };
      if (['/partners', '/productos', '/clasificacion-productos', '/proveedores', '/costos'].some((p) => path.startsWith(p))) {
        next.operaciones = true;
      }
      if (
        [
          '/proyectos',
          '/subproyectos_batch',
          '/proyectos_batch',
          '/detalle_proyectos',
          '/detalle-proyectos',
          '/lideres_proyectos',
          '/proyecto_con_causa',
          '/sedes',
          '/estado_instructor',
        ].some((p) => path.startsWith(p))
      ) {
        next.proyectos = true;
      }
      if (['/movimientos', '/BoxScore', '/box-score-historico', '/analytics', '/business-intelligence', '/bi-online', '/estado', '/dashboards', '/reportes'].some((p) => path.startsWith(p))) {
        next.finanzas = true;
      }
      if (['/ai', '/ai-admin'].some((p) => path.startsWith(p))) {
        next.ai = true;
      }
      return next;
    });
  }, [location.pathname]);

  // Close the mobile drawer on route change.
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const toggleCollapse = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(STORAGE_KEY, next ? '1' : '0');
      } catch (_) {}
      return next;
    });
  };

  const toggleGroup = (key) => {
    if (collapsed) {
      // Expand the sidebar when a collapsed group is clicked; opening flyouts
      // in collapsed mode is a bigger change we can add later.
      setCollapsed(false);
      try { window.localStorage.setItem(STORAGE_KEY, '0'); } catch (_) {}
    }
    setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleLogout = useCallback(async () => {
    await logout();
    navigate('/', { replace: true });
  }, [navigate]);

  if (!loggedInUser) return null;

  const isActive = (paths) => {
    if (typeof paths === 'string') paths = [paths];
    return paths.some((p) => location.pathname === p || location.pathname.startsWith(`${p}/`));
  };

  // Item = one leaf link inside a group (or a top-level link like Panel/Clientes).
  const Item = ({ to, icon, label, indent = false }) => {
    const active = isActive(to);
    return (
      <Link
        to={to}
        style={{
          ...styles.item,
          ...(indent ? styles.itemIndent : {}),
          ...(active ? styles.itemActive : {}),
          ...(collapsed ? styles.itemCollapsed : {}),
        }}
        title={collapsed ? label : undefined}
      >
        {icon && <span style={styles.itemIcon}>{icon}</span>}
        {!collapsed && <span style={styles.itemLabel}>{label}</span>}
        {active && !collapsed && <span style={styles.activeMarker} />}
      </Link>
    );
  };

  // Group header — toggles a section of Items.
  const Group = ({ id, icon, label, children }) => {
    const isOpen = openGroups[id];
    return (
      <div style={styles.group}>
        <button
          type="button"
          onClick={() => toggleGroup(id)}
          style={{
            ...styles.groupHeader,
            ...(collapsed ? styles.itemCollapsed : {}),
          }}
          title={collapsed ? label : undefined}
        >
          {icon && <span style={styles.itemIcon}>{icon}</span>}
          {!collapsed && (
            <>
              <span style={styles.itemLabel}>{label}</span>
              <IconChevron open={isOpen} />
            </>
          )}
        </button>
        {!collapsed && isOpen && <div style={styles.groupBody}>{children}</div>}
      </div>
    );
  };

  return (
    <>
      {/* Mobile top bar — visible only when the sidebar is a drawer */}
      <div className="lssi-mobile-topbar" style={styles.mobileTopBar}>
        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          style={styles.mobileMenuBtn}
          aria-label="Abrir menú"
        >
          <IconMenu />
        </button>
        <Link to="/home" style={styles.mobileBrand}>
          <img
            src="https://leansixsigmainstitute.org/wp-content/uploads/2023/11/cropped-LOGO-LSSI-ID.png"
            width="120"
            alt="LSSI"
          />
        </Link>
      </div>

      {/* Backdrop under the mobile drawer */}
      {mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          style={styles.backdrop}
          aria-hidden="true"
        />
      )}

      {/* Sidebar itself */}
      <aside
        className={`lssi-sidebar${mobileOpen ? ' lssi-sidebar-open' : ''}`}
        style={{
          ...styles.sidebar,
          // On mobile the sidebar is a drawer — always full-width regardless
          // of the desktop collapsed state (which persists in localStorage
          // and would otherwise render a 64px icon-only strip on phones).
          width: mobileOpen
            ? SIDEBAR_WIDTH_EXPANDED
            : collapsed
            ? SIDEBAR_WIDTH_COLLAPSED
            : SIDEBAR_WIDTH_EXPANDED,
        }}
      >
        {/* Brand + collapse toggle */}
        <div style={styles.brandRow}>
          {!collapsed && (
            <Link to="/home" style={styles.brandLink}>
              <img
                src="https://leansixsigmainstitute.org/wp-content/uploads/2023/11/cropped-LOGO-LSSI-ID.png"
                width="120"
                alt="LSSI"
                style={{ display: 'block' }}
              />
            </Link>
          )}
          <button
            type="button"
            onClick={toggleCollapse}
            style={{
              ...styles.collapseBtn,
              ...(collapsed ? { margin: '0 auto' } : {}),
            }}
            aria-label={collapsed ? 'Expandir barra lateral' : 'Colapsar barra lateral'}
            title={collapsed ? 'Expandir' : 'Colapsar'}
          >
            <IconMenu />
          </button>
        </div>

        <hr style={styles.divider} />

        {/* Nav */}
        <nav style={styles.nav}>
          <Item to="/home" icon={<IconPanel />} label="Panel" />
          <Item to="/clientes" icon={<IconClientes />} label="Clientes" />

          <Group id="operaciones" icon={<IconOperaciones />} label="Operaciones">
            <Item to="/partners" label="Equipo" indent />
            <Item to="/productos" label="Productos" indent />
            <Item to="/clasificacion-productos" label="Clasificación de productos" indent />
            <Item to="/proveedores" label="Proveedores" indent />
            <Item to="/costos" label="Costos" indent />
          </Group>

          <Group id="proyectos" icon={<IconProyectos />} label="Proyectos">
            <Item to="/proyectos" label="General" indent />
            <Item to="/subproyectos_batch" label="Bundle" indent />
            <Item to="/detalle_proyectos" label="Detalles" indent />
            <Item to="/lideres_proyectos" label="Líderes" indent />
            <Item to="/proyecto_con_causa" label="Con Causa" indent />
            <Item to="/sedes" label="Value Stream" indent />
            <Item to="/estado_instructor" label="Pagos" indent />
          </Group>

          <Group id="finanzas" icon={<IconFinanzas />} label="Finanzas">
            <Item to="/movimientos" label="Movimientos" indent />
            <Item to="/BoxScore" label="Box Score" indent />
            <Item to="/box-score-historico" label="Box Score Histórico" indent />
            <Item to="/analytics" label="Analytics" indent />
            <Item to="/reportes" label="Reportes" indent />
            <Item to="/estado" label="Estado del Proyecto" indent />
          </Group>

          {(chatbotAllowed || chatbotAdmin) && (
            <Group id="ai" icon={<IconAI />} label="AI">
              {chatbotAllowed && <Item to="/ai" label="Asistente" indent />}
              {chatbotAdmin && <Item to="/ai-admin" label="Log" indent />}
            </Group>
          )}

          {loggedInUser === 'salvador@lidigo.io' && (
            <Item to="/user-admin" icon={<IconShield />} label="Usuarios" />
          )}
        </nav>

        {/* User zone at the bottom */}
        <div style={styles.userZone}>
          {!collapsed ? (
            <>
              <div style={styles.userAvatar}>{(loggedInUser || '?').charAt(0).toUpperCase()}</div>
              <div style={styles.userInfo}>
                <div style={styles.userName} title={loggedInUser}>{loggedInUser}</div>
                <div style={styles.userActions}>
                  <Link to="/security-settings" style={styles.userAction}>
                    <IconShield /> Security
                  </Link>
                  <button type="button" onClick={handleLogout} style={styles.userActionBtn}>
                    <IconLogOut /> Salir
                  </button>
                </div>
              </div>
            </>
          ) : (
            <button
              type="button"
              onClick={handleLogout}
              style={styles.userAvatarBtn}
              title={`Cerrar sesión (${loggedInUser})`}
              aria-label="Cerrar sesión"
            >
              {(loggedInUser || '?').charAt(0).toUpperCase()}
            </button>
          )}
        </div>
      </aside>

      {/* Layout + responsive rules */}
      <style>{`
        /* Push page content past the sidebar AND shrink its width by the
           same amount, otherwise a 100%-wide .container plus margin-left
           extends off-screen to the right. */
        /* Fallback on :root (html) — JS also writes here in useEffect,
           so both live at the same level and JS updates win on re-render.
           Setting the fallback on body would shadow the JS value for every
           descendant, freezing the container margin at the expanded width. */
        :root { --lssi-sidebar-width: ${SIDEBAR_WIDTH_EXPANDED}px; }
        .App > .container {
          margin-left: var(--lssi-sidebar-width, 0px) !important;
          width: calc(100% - var(--lssi-sidebar-width, 0px)) !important;
          max-width: calc(100vw - var(--lssi-sidebar-width, 0px)) !important;
          transition: margin-left 0.2s ease, width 0.2s ease, max-width 0.2s ease;
          box-sizing: border-box;
        }
        .lssi-sidebar {
          transition: width 0.2s ease, transform 0.25s ease;
        }
        .lssi-sidebar a:hover,
        .lssi-sidebar button:hover {
          background: rgba(220, 53, 69, 0.06);
          color: ${BRAND_RED_DARK};
        }
        .lssi-mobile-topbar { display: none; }
        @media (max-width: 900px) {
          .lssi-mobile-topbar { display: flex !important; }
          .lssi-sidebar {
            transform: translateX(-100%);
            box-shadow: 0 20px 50px rgba(0,0,0,0.25);
            z-index: 1050;
          }
          .lssi-sidebar.lssi-sidebar-open {
            transform: translateX(0);
          }
          .App > .container {
            margin-left: 0 !important;
            width: 100% !important;
            max-width: 100vw !important;
            margin-top: 88px !important;
          }
        }
      `}</style>
    </>
  );
};

// -------------------- Styles --------------------
const styles = {
  sidebar: {
    position: 'fixed',
    top: 0,
    left: 0,
    height: '100vh',
    background: '#ffffff',
    borderRight: '1px solid #e5e7eb',
    display: 'flex',
    flexDirection: 'column',
    // Must stay above the mobile backdrop (1045) — inline z-index beats the
    // mobile CSS override, so it has to be right here, not just in the
    // media query. Otherwise taps on menu items hit the backdrop's onClick
    // and close the drawer without navigating.
    zIndex: 1050,
    fontFamily: '-apple-system, "Segoe UI", "Helvetica Neue", Arial, sans-serif',
    fontSize: '0.9rem',
    color: '#374151',
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  brandRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    // Collapsed sidebar has nothing here but the toggle button, so keep
    // the row compact — the old 16/12 padding + 56 min-height left a big
    // dead zone before the first nav icon.
    padding: '10px 12px',
    minHeight: 0,
  },
  brandLink: {
    display: 'inline-flex',
    alignItems: 'center',
    textDecoration: 'none',
  },
  collapseBtn: {
    background: 'transparent',
    border: 'none',
    padding: '8px',
    borderRadius: '8px',
    color: '#6b7280',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  divider: {
    margin: '0 12px 8px',
    border: 0,
    borderTop: '1px solid #e5e7eb',
  },
  nav: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    padding: '4px 8px 12px',
  },
  group: {
    display: 'flex',
    flexDirection: 'column',
  },
  groupHeader: {
    all: 'unset',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '9px 12px',
    borderRadius: '8px',
    cursor: 'pointer',
    color: '#374151',
    fontWeight: 500,
    letterSpacing: '0.01em',
  },
  groupBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    paddingLeft: '4px',
    marginTop: '2px',
  },
  item: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '8px 12px',
    borderRadius: '8px',
    color: '#374151',
    textDecoration: 'none',
    fontWeight: 500,
    letterSpacing: '0.01em',
    transition: 'background 0.15s ease, color 0.15s ease',
  },
  itemIndent: {
    padding: '7px 12px 7px 40px',
    fontWeight: 400,
    fontSize: '0.87rem',
    color: '#4b5563',
  },
  itemActive: {
    background: 'rgba(220, 53, 69, 0.08)',
    color: BRAND_RED_DARK,
    fontWeight: 600,
  },
  itemCollapsed: {
    justifyContent: 'center',
    padding: '10px',
  },
  itemIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#6b7280',
    width: '20px',
    flexShrink: 0,
  },
  itemLabel: {
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  activeMarker: {
    position: 'absolute',
    left: 0,
    top: '20%',
    bottom: '20%',
    width: '3px',
    borderRadius: '0 3px 3px 0',
    background: BRAND_RED,
  },
  userZone: {
    borderTop: '1px solid #e5e7eb',
    padding: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  userAvatar: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    background: BRAND_RED,
    color: '#ffffff',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: '0.95rem',
    flexShrink: 0,
  },
  userAvatarBtn: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    background: BRAND_RED,
    color: '#ffffff',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: '0.95rem',
    flexShrink: 0,
    border: 'none',
    cursor: 'pointer',
    margin: '0 auto',
  },
  userInfo: {
    flex: 1,
    minWidth: 0,
  },
  userName: {
    fontSize: '0.8125rem',
    fontWeight: 600,
    color: '#111827',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  userActions: {
    display: 'flex',
    gap: '10px',
    marginTop: '2px',
    fontSize: '0.75rem',
  },
  userAction: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    color: '#6b7280',
    textDecoration: 'none',
  },
  userActionBtn: {
    background: 'transparent',
    border: 'none',
    padding: 0,
    color: '#6b7280',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '0.75rem',
  },
  mobileTopBar: {
    display: 'none',
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    height: '64px',
    background: '#ffffff',
    borderBottom: '1px solid #e5e7eb',
    alignItems: 'center',
    gap: '12px',
    padding: '0 16px',
    zIndex: 1030,
  },
  mobileMenuBtn: {
    background: 'transparent',
    border: 'none',
    padding: '8px',
    borderRadius: '8px',
    color: '#374151',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  mobileBrand: {
    display: 'inline-flex',
    alignItems: 'center',
    textDecoration: 'none',
    marginLeft: '4px',
  },
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(15, 23, 42, 0.4)',
    zIndex: 1045,
  },
};

export default Sidebar;
